import { Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { FoodGuideService } from './food-guide.service';
import * as fromRoot from '../../../../state/app.state';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { CommonService } from '../../../shared/services/common.service';
import {
  FetchFoodGuideListRequest,
  FetchFoodGuideListSuccess,
  FetchFoodGuideListFailure,
  FetchFoodGuideItemRequest,
  FetchFoodGuideItemSuccess,
  FetchFoodGuideItemFailure,
  CreateFoodGuideItemRequest,
  CreateFoodGuideItemSuccess,
  CreateFoodGuideItemFailure,
  UpdateFoodGuideItemRequest,
  UpdateFoodGuideItemSuccess,
  UpdateFoodGuideItemFailure,
  RetireFoodGuideItemRequest,
  RetireFoodGuideItemSuccess,
  RetireFoodGuideItemFailure,
  UnretireFoodGuideItemRequest,
  UnretireFoodGuideItemSuccess,
  UnretireFoodGuideItemFailure,
  FetchFoodGuideHistoryRequest,
  FetchFoodGuideHistorySuccess,
  FetchFoodGuideHistoryFailure,
  FetchFoodCategoriesRequest,
  FetchFoodCategoriesSuccess,
  FetchFoodCategoriesFailure,
} from './food-guide.actions';

@Injectable()
export class FoodGuideEffects {

  fetchFoodGuideList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchFoodGuideListRequest),
      map((action: any) => action.payload),
      mergeMap((payload) =>
        this.foodGuideService.fetchFoodGuideList(payload).pipe(
          map((response: any) => {
            const { data, message } = response;
            return FetchFoodGuideListSuccess({ data, message });
          }),
          catchError(() => of(FetchFoodGuideListFailure())),
          tap((action: any) => {
            if (action.type === FetchFoodGuideListFailure.type) {
              // handle failure
            }
          })
        )
      )
    )
  );

  fetchFoodGuideItem$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchFoodGuideItemRequest),
      map((action: any) => action.payload),
      mergeMap((payload) =>
        this.foodGuideService.fetchFoodGuideItem(payload.id).pipe(
          map((response: any) => {
            const { data, message } = response;
            return FetchFoodGuideItemSuccess({ data, message });
          }),
          catchError(() => of(FetchFoodGuideItemFailure())),
        )
      )
    )
  );

  createFoodGuideItem$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(CreateFoodGuideItemRequest),
      map((action: any) => action.payload),
      mergeMap((payload) =>
        this.foodGuideService.createFoodGuideItem(payload).pipe(
          map((response: any) => {
            const { data, message } = response;
            return CreateFoodGuideItemSuccess({ data, message });
          }),
          catchError(() => of(CreateFoodGuideItemFailure())),
          tap((action: any) => {
            if (action.type === CreateFoodGuideItemSuccess.type) {
              this.commonService.closeDialog();
              this.commonService.notification(action.message, 'success');
            }
          })
        )
      )
    )
  );

  updateFoodGuideItem$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateFoodGuideItemRequest),
      map((action: any) => action.payload),
      mergeMap((payload) =>
        this.foodGuideService.updateFoodGuideItem(payload.id, payload).pipe(
          map((response: any) => {
            const { data, message } = response;
            return UpdateFoodGuideItemSuccess({ data, message });
          }),
          catchError(() => of(UpdateFoodGuideItemFailure())),
          tap((action: any) => {
            if (action.type === UpdateFoodGuideItemSuccess.type) {
              this.commonService.closeDialog();
              this.commonService.notification(action.message, 'success');
            }
          })
        )
      )
    )
  );

  retireFoodGuideItem$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(RetireFoodGuideItemRequest),
      map((action: any) => action.payload),
      mergeMap((payload) =>
        this.foodGuideService.retireFoodGuideItem(payload.id).pipe(
          map((response: any) => {
            const { message } = response;
            return RetireFoodGuideItemSuccess({ message });
          }),
          catchError(() => of(RetireFoodGuideItemFailure())),
          tap((action: any) => {
            if (action.type === RetireFoodGuideItemSuccess.type) {
              this.commonService.notification(action.message, 'success');
            }
          })
        )
      )
    )
  );

  unretireFoodGuideItem$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UnretireFoodGuideItemRequest),
      map((action: any) => action.payload),
      mergeMap((payload) =>
        this.foodGuideService.unretireFoodGuideItem(payload.id).pipe(
          map((response: any) => {
            const { message } = response;
            return UnretireFoodGuideItemSuccess({ message });
          }),
          catchError(() => of(UnretireFoodGuideItemFailure())),
          tap((action: any) => {
            if (action.type === UnretireFoodGuideItemSuccess.type) {
              this.commonService.notification(action.message, 'success');
            }
          })
        )
      )
    )
  );

  fetchFoodGuideHistory$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchFoodGuideHistoryRequest),
      map((action: any) => action.payload),
      mergeMap((payload) =>
        this.foodGuideService.fetchFoodGuideHistory(payload.id, {
          pageNumber: payload.pageNumber,
          limit: payload.limit,
        }).pipe(
          map((response: any) => {
            const { data, message } = response;
            return FetchFoodGuideHistorySuccess({ data, message });
          }),
          catchError(() => of(FetchFoodGuideHistoryFailure())),
        )
      )
    )
  );

  fetchFoodCategories$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchFoodCategoriesRequest),
      mergeMap(() =>
        this.foodGuideService.fetchFoodCategories().pipe(
          map((response: any) => {
            const { data } = response;
            return FetchFoodCategoriesSuccess({ data });
          }),
          catchError(() => of(FetchFoodCategoriesFailure())),
        )
      )
    )
  );

  constructor(
    private store: Store<fromRoot.State>,
    private foodGuideService: FoodGuideService,
    private commonService: CommonService,
    private actions: Actions,
  ) {
  }
}
